<template>
    <div class="booking-cancellation">
        <div class="row">
            <!-- Cancellation process -->
            <div class="col-lg-6 col-md-8 offset-md-2" :class="{ 'offset-lg-3': ! hasOptions, 'offset-lg-1': hasOptions }">
                <!-- Process -->
                <div class="card border mt-3" style="margin: 0 auto;" v-if="! isCancelled">
                    <div class="card-body" style="background-color: #ECF8F7;">
                        <!-- Illustration -->
                        <img :src="$cdn.default + 'illustrations/cancellation-start.svg?raw=true'" class="d-block mx-auto" style="max-width: 280px;" />

                        <h3 class="card-title text-center mt-3 mb-1">
                            {{ 'bookings.cancellation.title' | trans }}
                        </h3>
                        <p class="text-center mb-0">
                            {{ 'bookings.cancellation.subtitle' | trans }}
                        </p>
                    </div>

                    <!-- Goodwill -->
                    <div class="card-header text-center bg-success px-4 pt-3" v-if="details && details.booking.is_goodwill_cancellation">
                        <strong class="text-white">
                            <i class="fa fa-piggy-bank"></i>
                            {{ 'bookings.cancellation.goodwillMode' | trans }}
                        </strong>
                    </div>

                    <div class="card-body">
                        <div class="form-group">
                            <div class="btn-group btn-group-toggle w-100">
                                <!-- Full -->
                                <label class="btn btn-outline-primary btn-lg" for="mode_full" :class="{ active: mode == 'full' }">
                                    <input type="radio" name="mode" value="full" id="mode_full" autocomplete="off" v-model="mode">
                                    Komplett
                                </label>

                                <!-- Partial -->
                                <label class="btn btn-outline-primary btn-lg" for="mode_partial" :class="{ active: mode == 'partial' }">
                                    <input type="radio" name="mode" value="partial" id="mode_partial" autocomplete="off" v-model="mode">
                                    Teilweise
                                </label>
                            </div>
                        </div>

                        <!-- Loading -->
                        <div class="text-center py-5" v-if="! details">
                            <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                                <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                                <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                            </svg>
                        </div>

                        <!-- Full -->
                        <div v-if="details && mode === 'full'">
                            <p class="text-center">
                                <i class="fa fa-info-circle"></i>
                                {{ 'bookings.cancellation.fullModeInfo' | trans }}
                            </p>

                            <hr />

                            <!-- Reservations -->
                            <div class="form-group" v-if="details.reservations.length > 0">
                                <strong>Reservierungen</strong>

                                <p v-for="reservation in details.reservations">
                                    {{ reservation.room.hotel.name }}

                                    <small class="d-block">
                                        {{ reservation.room.name }}
                                        &bull;
                                        {{ $moment(reservation.travel_period.arrive_date).format('DD.MM.YYYY') }}
                                        bis
                                        {{ $moment(reservation.travel_period.departure_date).format('DD.MM.YYYY') }}
                                    </small>
                                </p>
                            </div>

                            <!-- Ticket bookings -->
                            <div class="form-group" v-if="details.ticket_bookings.length > 0">
                                <strong>Tickets</strong>

                                <p v-for="ticketBooking in details.ticket_bookings">
                                    {{ ticketBooking.ticket.name }}

                                    <small class="d-block">
                                        {{ ticketBooking.ticket.subtitle }}
                                    </small>
                                </p>
                            </div>

                            <!-- Flight bookings -->
                            <div class="form-group" v-if="details.flight_bookings.length > 0">
                                <strong>Flüge</strong>

                                <p v-for="flightBooking in details.flight_bookings">
                                    <span class="d-block" v-for="segmentGroup in flightBooking.segment_groups">
                                        {{ segmentGroup.segments.map(segment => segment.flight_number + ' (' + segment.departure_airport_code + '-' + segment.arrival_airport_code + ')').join(', ') }}
                                    </span>
                                </p>
                            </div>

                            <!-- Product purchases -->
                            <div class="form-group" v-if="details.purchases.length > 0">
                                <strong>Gutscheine</strong>

                                <p v-for="purchase in details.purchases">
                                    {{ purchase.product.name }}

                                    <small class="d-block">
                                        {{ purchase.product.subtitle }}
                                    </small>
                                </p>
                            </div>

                            <!-- Shipments -->
                            <div class="form-group" v-if="details.shipments.length > 0">
                                <strong>Sendungen</strong>

                                <p v-for="shipment in details.shipments">
                                    {{ shipment.shipping_variant ? shipment.shipping_variant.name : 'Versandoption' }}

                                    <small class="d-block">
                                        {{ shipment.address }}
                                    </small>
                                </p>
                            </div>

                            <!-- Insurance contracts -->
                            <div class="form-group" v-if="details.insurance_contracts.length > 0">
                                <strong>Versicherungen</strong>

                                <p v-for="insuranceContract in details.insurance_contracts">
                                    {{ insuranceContract.insurance.name }}
                                </p>
                            </div>
                        </div>

                        <!-- Partial -->
                        <div v-if="details && mode === 'partial'">
                            <p class="text-center">
                                <i class="fa fa-info-circle"></i>
                                {{ 'bookings.cancellation.chooseServicesInfo' | trans }}
                            </p>

                            <hr />

                            <!-- Reservations -->
                            <div class="form-group" v-if="details.reservations.length > 0">
                                <strong>Reservierungen</strong>

                                <div class="d-flex" v-for="reservation in details.reservations">
                                    <div class="w-100">
                                        <!-- Checkbox -->
                                        <checkbox :key="reservation.id" :label="reservation.room.hotel.name" :subname="reservation.room.name" name="reservations" @input="toggle(checkedReservations, reservation.id, $event)" />

                                        <!-- Input -->
                                        <div class="input-group ml-4 mt-n3 mb-3" v-if="isCustomCostsInput('reservation' + reservation.id)">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text px-3 pt-1 border-right" style="height: 40px;">
                                                    Einkaufspreis
                                                </span>
                                            </div>

                                            <input type="text" class="form-control pt-1" @change="toggleCancellationCosts(reservationCosts, reservation.id, $event)" placeholder="z.B. 25,50" style="height: 40px;" />

                                            <div class="input-group-append">
                                                <span class="input-group-text pt-1" style="height: 40px;">
                                                    &euro;
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex-shrink-1">
                                        <!-- Edit -->
                                        <button type="button" class="btn btn-link" @click.prevent="toggleCustomCostsInput('reservation', reservation.id)" v-if="hasOption('custom_costs')">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Ticket bookings -->
                            <div class="form-group" v-if="details.ticket_bookings.length > 0">
                                <strong>Tickets</strong>

                                <div class="d-flex" v-for="ticketBooking in details.ticket_bookings">
                                    <div class="w-100">
                                        <!-- Checkbox -->
                                        <checkbox :key="ticketBooking.id" :label="ticketBooking.ticket.name" :subname="ticketBooking.ticket.subtitle" name="ticket_bookings" @input="toggle(checkedTicketBookings, ticketBooking.id, $event)" />

                                        <!-- Input -->
                                        <div class="input-group ml-4 mt-n3 mb-3" v-if="isCustomCostsInput('ticketBooking' + ticketBooking.id)">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text px-3 pt-1 border-right" style="height: 40px;">
                                                    Einkaufspreis
                                                </span>
                                            </div>

                                            <input type="text" class="form-control pt-1" @change="toggleCancellationCosts(ticketBookingCosts, ticketBooking.id, $event)" placeholder="z.B. 25,50" style="height: 40px;" />

                                            <div class="input-group-append">
                                                <span class="input-group-text pt-1" style="height: 40px;">
                                                    &euro;
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex-shrink-1">
                                        <!-- Edit -->
                                        <button type="button" class="btn btn-link" @click.prevent="toggleCustomCostsInput('ticketBooking', ticketBooking.id)" v-if="hasOption('custom_costs')">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Flight bookings -->
                            <div class="form-group" v-if="details.flight_bookings.length > 0">
                                <strong>Flüge</strong>

                                <div class="d-flex" v-for="flightBooking in details.flight_bookings">
                                    <div class="w-100">
                                        <!-- Checkbox -->
                                        <checkbox :key="flightBooking.id" :label="flightBooking.segment_groups.map(segmentGroup => segmentGroup.segments.map(segment => segment.flight_number + ' (' + segment.departure_airport_code + '-' + segment.arrival_airport_code + ')').join(', ')).join(', ')" name="flight_bookings" @input="toggle(checkedFlightBookings, flightBooking.id, $event)" />

                                        <!-- Input -->
                                        <div class="input-group ml-4 mt-n3 mb-3" v-if="isCustomCostsInput('flightBooking' + flightBooking.id)">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text px-3 pt-1 border-right" style="height: 40px;">
                                                    Einkaufspreis
                                                </span>
                                            </div>

                                            <input type="text" class="form-control pt-1" @change="toggleCancellationCosts(flightBookingCosts, flightBooking.id, $event)" placeholder="z.B. 25,50" style="height: 40px;" />

                                            <div class="input-group-append">
                                                <span class="input-group-text pt-1" style="height: 40px;">
                                                    &euro;
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex-shrink-1">
                                        <!-- Edit -->
                                        <button type="button" class="btn btn-link" @click.prevent="toggleCustomCostsInput('flightBooking', flightBooking.id)" v-if="hasOption('custom_costs')">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Product purchases -->
                            <div class="form-group" v-if="details.purchases.length > 0">
                                <strong>Gutscheine</strong>

                                <div class="d-flex" v-for="purchase in details.purchases">
                                    <div class="w-100">
                                        <!-- Checkbox -->
                                        <checkbox :key="purchase.id" :label="purchase.product.name" :subname="purchase.product.subtitle" name="product_purchases" @input="toggle(checkedProductPurchases, purchase.id, $event)" />

                                        <!-- Input -->
                                        <div class="input-group ml-4 mt-n3 mb-3" v-if="isCustomCostsInput('productPurchase' + purchase.id)">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text px-3 pt-1 border-right" style="height: 40px;">
                                                    Einkaufspreis
                                                </span>
                                            </div>

                                            <input type="text" class="form-control pt-1" @change="toggleCancellationCosts(productPurchaseCosts, purchase.id, $event)" placeholder="z.B. 25,50" style="height: 40px;" />

                                            <div class="input-group-append">
                                                <span class="input-group-text pt-1" style="height: 40px;">
                                                    &euro;
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex-shrink-1">
                                        <!-- Edit -->
                                        <button type="button" class="btn btn-link" @click.prevent="toggleCustomCostsInput('productPurchase', purchase.id)" v-if="hasOption('custom_costs')">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Shipments -->
                            <div class="form-group" v-if="details.shipments.length > 0">
                                <strong>Sendungen</strong>

                                <checkbox :key="shipment.id" :label="shipment.shipping_variant ? shipment.shipping_variant.name : 'Versandoption'" :subname="shipment.address" name="shipments" v-for="shipment in details.shipments" @input="toggle(checkedShipments, shipment.id, $event)" />
                            </div>

                            <!-- Insurance contracts -->
                            <div class="form-group" v-if="details.insurance_contracts.length > 0">
                                <strong>Versicherungen</strong>

                                <checkbox :key="insuranceContract.id" :label="insuranceContract.insurance.name" :subname="insuranceContract.insurance.insurance_company" name="insurance_contracts" v-for="insuranceContract in details.insurance_contracts" @input="toggle(checkedInsuranceContracts, insuranceContract.id, $event)" />
                            </div>
                        </div>
                    </div>

                    <!-- Rules -->
                    <div class="card-footer px-0" v-if="details && details.rules.length > 0">
                        <div class="list-group list-group-flush">
                            <div class="list-group-item" v-for="rule in details.rules" style="background-color: transparent;">
                                <div class="d-flex w-100 justify-content-between">
                                    <span>
                                        {{ 'bookings.cancellation.ruleTypes.' + rule.type | trans }}
                                    </span>

                                    <span>
                                        {{ rule.amount | formatPrice }}
                                        <span v-if="rule.amount_type === 'amount'">&euro;</span>
                                        <span v-if="rule.amount_type !== 'amount'">%</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card-footer">
                        <!-- Loading -->
                        <div class="text-center py-3" v-if="isLoading">
                            <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                                <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                                <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                            </svg>
                        </div>

                        <div class="row" v-if="! isLoading && details">
                            <div class="col" v-if="hasOption('custom_costs')">
                                <!-- Costs -->
                                <p class="lead text-center font-weight-700">
                                    {{ details.costs.total | formatPrice }}
                                    &euro;

                                    <small class="d-block text-muted">
                                        Intern: {{ details.costs.internal_total | formatPrice }}
                                    </small>

                                    <small class="d-block">
                                        Kosten
                                    </small>
                                </p>
                            </div>

                            <div class="col">
                                <!-- Fees -->
                                <p class="lead text-center font-weight-700">
                                    <span v-if="! isCustomCostsInput('fee')">
                                        {{ details.costs.fee | formatPrice }}
                                        &euro;
                                    </span>

                                    <!-- Input -->
                                    <input type="text" class="form-control pt-2 px-1 text-center d-inline-block mb-1" :value="fee" @change="({target: { value }}) => {
                                        this.fee = value
                                    }" placeholder="z.B. 5" style="height: 40px; width: 70px;" v-if="isCustomCostsInput('fee')" />

                                    <!-- Edit fee -->
                                    <button type="button" class="btn btn-link btn-sm" @click.prevent="toggleCustomCostsInput('fee', '')" v-if="hasOption('custom_costs')">
                                        <i class="fa fa-pencil"></i>
                                    </button>

                                    <small class="d-block">
                                        Gebühr
                                    </small>
                                </p>
                            </div>

                            <div class="col">
                                <!-- Refund -->
                                <p class="lead text-center font-weight-700" :class="{ 'text-success': details.costs.refund > 0 }">
                                    {{ details.costs.refund | formatPrice }}
                                    &euro;

                                    <small class="d-block" :class="{ 'text-success': details.costs.refund > 0 }">
                                        Rückerstattung
                                    </small>
                                </p>
                            </div>
                        </div>

                        <!-- Next button -->
                        <button type="submit" class="btn btn-danger btn-lg btn-block" :disabled="! canCancel" @click="cancel">
                            <i class="wa-icon-long-arrow-right mt-1 float-right"></i>
                            Stornierung bestätigen
                        </button>

                        <small class="d-block text-center text-muted mt-2">
                            Deine Stornierung kann nicht rückgängig gemacht werden.
                        </small>
                    </div>
                </div>

                <!-- Confirmation -->
                <div class="card border mt-3" style="margin: 0 auto;" v-if="isCancelled">
                    <div class="card-body" style="background-color: #ECF8F7;">
                        <!-- Illustration -->
                        <img :src="$cdn.default + 'illustrations/cancellation-no-refund.svg?raw=true'" class="d-block mx-auto" v-if="details.costs.refund == 0" style="max-width: 280px;" />
                        <img :src="$cdn.default + 'illustrations/cancellation-refund.svg?raw=true'" class="d-block mx-auto" v-if="details.costs.refund > 0" style="max-width: 280px;" />

                        <h3 class="card-title text-center mt-3 mb-1">
                            {{ 'bookings.cancellation.confirmationTitle' | trans }}
                        </h3>
                        <p class="text-center mb-0">
                            {{ 'bookings.cancellation.confirmationSubtitle' | trans }}
                        </p>

                        <div v-if="details.costs.refund > 0">
                            <hr />
                            <p class="text-center lead">
                                {{ details.costs.refund | formatPrice }}
                                &euro;

                                <small class="d-block">
                                    {{ 'bookings.cancellation.confirmationRefund' | trans }}
                                </small>
                            </p>
                        </div>

                        <!-- Invoice link -->
                        <a class="btn btn-primary btn-block btn-lg mt-3" :href="invoiceLink" target="_blank" style="height: auto;">
                            <i class="fa fa-cloud-download"></i>
                            {{ 'bookings.cancellation.openInvoice' | trans }}
                        </a>
                    </div>
                </div>
            </div>

            <!-- Options -->
            <div class="col-lg-4" v-if="hasOptions && ! hasOption('faq')">
                <div class="card border mt-3">
                    <!-- Canceled at -->
                    <div class="card-footer border-top-0" v-if="hasOption('goodwill') || hasOption('ignore_voucher_rules')">
                        <strong class="d-block">
                            Storniert am
                        </strong>

                        <datepicker v-model="canceledAt" :full-month-name="true" :language="language" input-class="form-control form-control-lg" :monday-first="true" :format="'dd.MM.yyyy'"></datepicker>

                        <small class="d-block text-muted mt-2">
                            {{ canceledAgo }}
                        </small>
                    </div>

                    <!-- Cancellation reason -->
                    <div class="card-footer" v-if="details">
                        <strong class="d-block">
                            Stornierungsgrund
                        </strong>

                        <select class="custom-select" v-model="options.cancellation_reason_id">
                            <option :value="null">
                                Kein Grund
                            </option>
                            <option :value="cancellationReason.id" v-for="cancellationReason in details.cancellation_reasons">
                                {{ cancellationReason.title }}
                            </option>
                        </select>
                    </div>

                    <!-- Cancellation rules -->
                    <div class="card-footer pb-0" v-if="hasOption('goodwill') || hasOption('ignore_voucher_rules')">
                        <strong class="d-block">
                            Stornoregeln
                        </strong>

                        <!-- Goodwill -->
                        <checkbox icon="fa fa-smile-beam" label="Kulanzmodus" subname="Alle offiziellen Stornoregeln werden ignoriert." name="is_goodwill" @input="options.is_goodwill = $event" />

                        <!-- Ignore voucher rules -->
                        <checkbox label="Gutschein-Stornoregel ignorieren" subname="Ignoriert die 100 % Regel für Gutscheineinlösungen." name="is_ignore_voucher_rules" @input="options.is_ignore_voucher_rules = $event" />
                    </div>

                    <!-- Refund preview -->
                    <div class="card-footer" v-if="hasOption('refund_preview') || hasOption('release_voucher_code')">
                        <strong class="d-block">
                            <!-- Total -->
                            <span class="float-right">
                                Gesamt
                                {{ details.booking.price.total | formatPrice }}
                                &euro;
                            </span>

                            Zahlungen
                        </strong>

                        <!-- Refund extra charge only -->
                        <checkbox label="Nur Aufpreis erstatten" subname="Limitiert die Höhe der Rückerstattung und den Aufpreis der Einlösung." name="is_refund_extra_charge_only" @input="options.is_refund_extra_charge_only = $event" code-label="Gutscheinwert" :code="details.voucher_codes[0].voucher.price" v-if="hasOption('refund_extra_charge_only') && details.booking.is_voucher_redemption && details.voucher_codes.length > 0" />

                        <!-- Release voucher code -->
                        <checkbox label="Gutschein freigeben" subname="Gibt den Gutscheincode für eine erneute Einlösung frei." name="is_release_voucher_code" @input="options.is_release_voucher_code = $event" :code="details.voucher_codes[0].code" v-if="hasOption('release_voucher_code') && details.booking.is_voucher_redemption && details.voucher_codes.length > 0" />

                        <!-- Refund as value coupon -->
                        <checkbox label="Wertgutschein erstellen " subname="Wandelt alle regulären Rückerstattungen in einen Wertgutschein um." name="is_refund_as_value_coupon" @input="options.is_refund_as_value_coupon = $event" v-if="hasOption('refund_as_value_coupon')" />

                        <!-- No payments -->
                        <small class="d-block text-muted" v-if="details.refund_preview.length === 0">
                            Keine Vorschau von Rückerstattungen vorhanden.
                        </small>

                        <!-- List payments -->
                        <ul class="list-group" v-if="details.refund_preview.length > 0">
                            <li class="list-group-item" v-for="payment in details.refund_preview">
                                <div class="d-flex w-100 justify-content-between">
                                    <strong>
                                        <!-- Incoming -->
                                        <i class="fa fa-long-arrow-left text-success mr-1" v-if="payment.type === 'incoming'"></i>

                                        <!-- Outgoing -->
                                        <i class="fa fa-long-arrow-right text-danger mr-1" v-if="payment.type === 'outgoing'"></i>

                                        {{ payment.name }}
                                    </strong>
                                    <small>
                                        {{ payment.subtotal | formatPrice}}
                                        &euro;
                                    </small>
                                </div>

                                <small>
                                    {{ 'backend.service.bookings.paymentType.' + payment.payment_type | trans }}
                                    <span class="px-2">&bull;</span>
                                    {{ 'backend.service.bookings.paymentStatus.' + payment.status | trans }}
                                </small>
                            </li>
                        </ul>
                    </div>

                    <!-- Notifications -->
                    <div class="card-footer pb-0" v-if="hasOption('notifications')">
                        <strong class="d-block">
                            Benachrichtigungen
                        </strong>

                        <checkbox :key="notification.target_type + notification.target_id + '_' + notificationIndex" :label="notification.description" :subname="notification.name" name="notification" v-for="(notification, notificationIndex) in details.notifications" :code="notification.email" code-label="E-Mail-Adresse" v-model="notification.checked" />
                    </div>
                </div>
            </div>

            <!-- FAQ -->
            <div class="col-lg-4" v-if="hasOption('faq')">
                <h5 class="mt-5">
                    Kundenservice
                </h5>
                <p>
                    Du benötigst Hilfe bei deiner Stornierung? Wir helfen dir gerne weiter!
                </p>
                <a class="btn btn-primary btn-block btn-lg" :href="$window.route('frontend.support.index')" target="_blank">
                    Zum Hilfe-Center
                </a>
                <a class="btn btn-outline-primary btn-block" :href="$window.route('frontend.support.contactForm.show')" target="_blank">
                    Nachricht schreiben
                </a>

                <div class="divider"></div>

                <h5 class="mt-3 mb-0">
                    Häufig gestellte Fragen
                </h5>

                <a class="d-block py-3 text-dark" data-toggle="collapse" href="#faq_1">
                    Wie erhalte ich eine Rückerstattung?
                </a>
                <div class="collapse show" id="faq_1">
                    <small class="text-muted d-block mb-3">
                        Deine angezeigte Rückerstattung erfolgt über das gleiche Zahlungsmittel, welches du bei deiner Buchung verwendet hast. Bei Zahlung per Kreditkarte, Sofort oder Klarna erhältst du von uns nach deiner Stornierung eine E-Mail mit weiteren Schritten zu deiner Rückerstattung.
                    </small>
                </div>

                <hr class="my-0" />

                <a class="d-block py-3 text-dark" data-toggle="collapse" href="#faq_2">
                    Was bedeutet "Gutscheineinlösung - 100 %"?
                </a>
                <div class="collapse" id="faq_2">
                    <small class="text-muted d-block mb-3">
                        Da du für deine Buchung einen Reisegutschein verwendet hast, ist in diesem Fall leider keine kostenfreie Umbuchung oder Stornierung möglich, da wir für Gutscheineinlösungen spezielle Kontingente vorgesehen haben, welche auch für uns nicht kostenfrei stornierbar sind.
                    </small>
                </div>

                <hr class="my-0" />

                <a class="d-block py-3 text-dark" data-toggle="collapse" href="#faq_3">
                    Warum erhalte ich für meine Tickets keine Rückerstattung?
                </a>
                <div class="collapse" id="faq_3">
                    <small class="text-muted d-block mb-3">
                        Tickets sind oftmals bereits mit einem gültigen Barcode generiert worden, sodass wir diesen nicht mehr beim Veranstalter kostenfrei stornieren können. Oft ist es jedoch so, dass die Tickets ihre Gültigkeit behalten und du diese innerhalb eines Jahres verwenden kannst. Prüfe hierfür bitte die Angabe auf deinem Ticket.
                    </small>
                </div>

                <hr class="my-0" />

                <a class="d-block py-3 text-dark" data-toggle="collapse" href="#faq_4">
                    Wieso sehe ich keine Sendungen?
                </a>
                <div class="collapse" id="faq_4">
                    <small class="text-muted d-block mb-3">
                        Sendungen sind nur stornierbar, wenn diese noch nicht versendet wurden. Sobald diese von uns an die Deutsche Post übergeben wurden, können diese leider nicht mehr kostenfrei storniert werden, da bereits Kosten für den Versand entstanden sind.
                    </small>
                </div>

                <hr class="my-0" />

                <a class="d-block py-3 text-dark" data-toggle="collapse" href="#faq_5">
                    Wann entstehen Gebühren?
                </a>
                <div class="collapse" id="faq_5">
                    <small class="text-muted d-block mb-3">
                        Wir erheben Stornierungsgebühren, wenn wir deine Buchung nach deiner Stornierung manuell beim Leistungsträger stornieren müssen und hierdurch unser Team aktiv deine Buchung bearbeiten muss.
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Checkbox from './../shared/form/Checkbox.vue'
    import Datepicker from 'vuejs-datepicker'
    import {de} from 'vuejs-datepicker/dist/locale'

    export default {
        props: ['detailsUrl', 'cancelUrl', 'supportPhone'],
        components: {
            Checkbox,
            Datepicker
        },
        data() {
            return {
                mode: 'full',
                details: null,
                isLoading: false,
                customCosts: [],

                // Confirmation
                isCancelled: false,
                invoiceLink: null,

                // Options
                options: {
                    is_goodwill: false,
                    is_ignore_voucher_rules: false,
                    is_release_voucher_code: false,
                    is_refund_extra_charge_only: false,
                    is_refund_as_value_coupon: false,
                    cancellation_reason_id: null,
                },

                // Checked
                checkedReservations: [],
                checkedTicketBookings: [],
                checkedFlightBookings: [],
                checkedProductPurchases: [],
                checkedShipments: [],
                checkedInsuranceContracts: [],

                // Costs
                reservationCosts: {},
                ticketBookingCosts: {},
                flightBookingCosts: {},
                productPurchaseCosts: {},
                fee: null,

                // Caneled at
                canceledAt: new Date(),
                language: de,
            }
        },
        mounted() {
            this.loadDetails(true)
        },
        computed: {
            canCancel() {
                return this.isLoading === false && (this.checkedReservations.length > 0 || this.checkedTicketBookings.length > 0 || this.checkedFlightBookings.length > 0 || this.checkedProductPurchases.length > 0 || this.checkedShipments.length > 0 || this.checkedInsuranceContracts.length > 0)
            },
            hasOptions() {
                return (this.details && this.details.options.length > 0) || this.details === null
            },
            canceledAgo() {
                return moment(this.canceledAt).fromNow()
            }
        },
        methods: {
            loadDetails(init = false) {
                var self = this;

                this.isLoading = true

                // Fee
                var fee = null
                if (this.fee !== null) {
                    fee  = ('' + this.fee).replace(',', '.').trim().replace('€', '')
                    fee = parseFloat(fee, 10)
                }

                // Options
                let _options = {...this.options, ...{
                    canceled_at: this.canceledAt ? moment(this.canceledAt).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                }}

                axios.post(this.detailsUrl, {
                    options: _options,
                    reservations: this.checkedReservations,
                    ticket_bookings: this.checkedTicketBookings,
                    flight_bookings: this.checkedFlightBookings,
                    product_purchases: this.checkedProductPurchases,
                    shipments: this.checkedShipments,
                    insurance_contracts: this.checkedInsuranceContracts,
                    costs: {
                        fee: fee,
                        reservations: Object.keys(this.reservationCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.reservationCosts[id],
                            }
                        }),
                        ticket_bookings: Object.keys(this.ticketBookingCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.ticketBookingCosts[id],
                            }
                        }),
                        flight_bookings: Object.keys(this.flightBookingCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.flightBookingCosts[id],
                            }
                        }),
                        product_purchases: Object.keys(this.productPurchaseCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.productPurchaseCosts[id],
                            }
                        })
                    }
                }).then((response) => {
                    self.details = response.data.data

                    // Checked
                    if (self.mode === 'full' && init) {
                        self.checkedReservations = self.details.reservations.map((reservation) => reservation.id)
                        self.checkedTicketBookings = self.details.ticket_bookings.map((ticketBooking) => ticketBooking.id)
                        self.checkedFlightBookings = self.details.flight_bookings.map((flightBooking) => flightBooking.id)
                        self.checkedProductPurchases = self.details.purchases.map((purchase) => purchase.id)
                        self.checkedShipments = self.details.shipments.map((shipment) => shipment.id)
                        self.checkedInsuranceContracts = self.details.insurance_contracts.map((insuranceContract) => insuranceContract.id)

                        // Selected options
                        self.options = {...self.options, ...self.details.selected_options}

                        // Filled costs
                        Object.keys(self.details.filled_costs).forEach((key) => {
                            self[key] = {...self[key], ...self.details.filled_costs[key]}
                        })

                        // Reload details
                        self.loadDetails()
                    }

                    self.isLoading = false
                }).catch((error) => {
                    console.log(error);
                    self.$toast.error('Error. Please check the console.');
                });
            },

            cancel() {
                var self = this;

                this.isLoading = true

                // Fee
                var fee = null
                if (this.fee !== null) {
                    fee  = ('' + this.fee).replace(',', '.').trim().replace('€', '')
                    fee = parseFloat(fee, 10)
                }

                // Options
                let _options = {...this.options, ...{
                    canceled_at: this.canceledAt ? moment(this.canceledAt).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                }}

                axios.post(this.cancelUrl, {
                    options: _options,
                    reservations: this.checkedReservations,
                    ticket_bookings: this.checkedTicketBookings,
                    flight_bookings: this.checkedFlightBookings,
                    product_purchases: this.checkedProductPurchases,
                    shipments: this.checkedShipments,
                    insurance_contracts: this.checkedInsuranceContracts,
                    costs: {
                        fee: fee,
                        reservations: Object.keys(this.reservationCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.reservationCosts[id],
                            }
                        }),
                        ticket_bookings: Object.keys(this.ticketBookingCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.ticketBookingCosts[id],
                            }
                        }),
                        flight_bookings: Object.keys(this.flightBookingCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.flightBookingCosts[id],
                            }
                        }),
                        product_purchases: Object.keys(this.productPurchaseCosts).map((id) => {
                            return {
                                id: id,
                                costs: self.productPurchaseCosts[id],
                            }
                        })
                    },
                    is_no_notifications: this.details.notifications.filter((notification) => notification.checked === true).length === 0,
                    notifications: this.details.notifications.filter((notification) => notification.checked === true).map((notification) => {
                        return {
                            target_type: notification.target_type,
                            target_id: notification.target_id,
                        }
                    })
                }).then((response) => {
                    self.isCancelled = response.data.data.is_cancelled

                    // Scroll to top
                    $('html, body').animate({
                        scrollTop: $('div.booking-cancellation').offset().top
                    }, 1000)

                    // Invoice link
                    if (self.isCancelled) {
                        self.invoiceLink = response.data.data.invoice_link
                    }

                    self.isLoading = false
                }).catch((error) => {
                    console.log(error);
                    self.$toast.error('Error. Please check the console.');
                });
            },

            toggle(target, id, checked) {
                if (checked) {
                    target.push(id)
                } else {
                    target.splice(target.indexOf(id), 1)
                }

                this.loadDetails()
            },

            hasOption(option) {
                return this.details && this.details.options && this.details.options.indexOf(option) !== -1
            },

            toggleCustomCostsInput(prefix, id) {
                let identifier = prefix + id

                var index = this.customCosts.indexOf(identifier)

                if (index !== -1) {
                    this.customCosts.splice(index, 1)

                    // Remove from custom costs
                    switch (prefix) {
                        case 'reservation': delete this.reservationCosts[id]; break;
                        case 'ticketBooking': delete this.ticketBookingCosts[id]; break;
                        case 'flightBooking': delete this.flightBookingCosts[id]; break;
                        case 'productPurchase': delete this.productPurchaseCosts[id]; break;
                        case 'fee': this.fee = null; break;
                    }
                } else {
                    this.customCosts.push(identifier)
                }

                this.loadDetails()
            },
            isCustomCostsInput(identifier) {
                return this.customCosts.indexOf(identifier) !== -1
            },
            toggleCancellationCosts(target, id, { target: { value } }) {
                var amount = ('' + value).replace(',', '.').trim().replace('€', '')
                amount = parseFloat(amount, 10)

                target[id] = amount

                this.loadDetails()
            }
        },
        watch: {
            mode() {
                if (this.mode === 'full') {
                    this.checkedReservations = this.details.reservations.map((reservation) => reservation.id)
                    this.checkedTicketBookings = this.details.ticket_bookings.map((ticketBooking) => ticketBooking.id)
                    this.checkedFlightBookings = this.details.flight_bookings.map((flightBooking) => flightBooking.id)
                    this.checkedProductPurchases = this.details.purchases.map((purchase) => purchase.id)
                    this.checkedShipments = this.details.shipments.map((shipment) => shipment.id)
                    this.checkedInsuranceContracts = this.details.insurance_contracts.map((insuranceContract) => insuranceContract.id)
                } else {
                    this.checkedReservations = []
                    this.checkedTicketBookings = []
                    this.checkedFlightBookings = []
                    this.checkedProductPurchases = []
                    this.checkedShipments = []
                    this.checkedInsuranceContracts = []
                }

                this.loadDetails()
            },

            options: {
                handler(val) {
                    this.loadDetails()
                },
                deep: true
            },
            fee() {
                this.loadDetails()
            },
            canceledAt() {
                this.loadDetails()
            },
        }
    }
</script>
